import { Menu, Modal } from '@mantine/core'
import PrimaryDropDown from 'app/views/components/DropDown/DropDown'
import { useState } from 'react'
import { DealParams } from 'app/models/deal-params'
import { useDispatch } from 'react-redux'
import {
  approveDeal,
  deleteDeal,
  rejectDeal,
  submitDeal,
} from './deal-daml-actions'
import {
  deleteDealAmendment,
  submitDealAmendment,
  approveDealAmendment,
  rejectDealAmendment,
} from '../deal-amendment-details/deal-amendment-daml-actions'
import { loadOneDealAmendment } from 'app/state/ducks/deal-amendments/thunks'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import HistoryForm from './history/history'
import { useNavigate } from 'react-router-dom'
import { UpdatedFacilitiesScreen } from '../../facilities/updated-facilities-screen'
import DealTerminationForm from '../locked-deals/deal-termination-form'
import IonIcon from '@reacticons/ionicons'
import DealsForm from './deal-form'
import { loadDealSnapshot, loadOneDeal } from 'app/state/ducks/deals/thunks'
import { generateHTML } from './../preview/generateHTML';
import { FacilitySnapshotParams } from 'app/models/snapshot-params'
import { formatDateSnapshot, formatNumberToCurrency } from 'app/utils/util-functions'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation'
import { loadDealFormLists, loadFacilityFormLists } from 'app/state/utils/form-lists'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'

type Props = {
  row: DealParams
  withRedirection?: boolean
  extended?: boolean
  primaryInput?: boolean
}

export default function DealActions({ row, withRedirection, extended, primaryInput }: Props) {
  const [openEdit, setOpenEdit] = useState(false)
  const [deal, setDeal] = useState<DealParams | undefined>(undefined)
  const [dealAmendment, setDealAmendment] = useState<DealParams | null>(null)
  const [openFacility, setOpenFacility] = useState(false)
  const [amendment, setAmendment] = useState<string | null>(null)
  const [openHistory, setOpenHistory] = useState(false)
  const [openTerminate, setOpenTerminate] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteType, setDeleteType] = useState<'deal' | 'amendment' | null>(null); // State for delete type
  const [loadingForm, setLoadingForm] = useState(false)
  const [loadingFacilityForm, setLoadingFacilityForm] = useState(false)

  // The forms behind this menu are built once from the record they open with,
  // so the record is fetched FIRST and the form opened with it. Fetched by id,
  // not read from the deal list: the grid is one projected read (ADR_LOS-030)
  // and the page that hosts it loads no list at all — the lists the form
  // offers in its dropdowns are loaded here, together with the record.

  /** The deal in the given state. Undefined, with a notice, when it cannot be loaded. */
  async function fetchDeal(status: string): Promise<DealParams | undefined> {
    const result: any = await dispatch(loadOneDeal(row.id, status))
    if (!result?.payload?.id) {
      ErrorNotification({
        title: 'Could not open deal',
        message: typeof result?.payload === 'string' ? result.payload : 'The deal could not be loaded.',
      })
      return undefined
    }
    return result.payload
  }

  /**
   * The amendment standing in for the deal, by its own id — on a row the read
   * returned and on one a termination created in this session (the terminate
   * answer carries the amendment's id). The deal's id is only a fallback.
   */
  async function fetchAmendment(): Promise<DealParams | undefined> {
    const result: any = await dispatch(
      loadOneDealAmendment(row.transactionId ?? row.id, row.status ?? 'Draft'),
    )
    if (!result?.payload?.id) {
      ErrorNotification({
        title: 'Could not open amendment',
        message: typeof result?.payload === 'string' ? result.payload : 'The amendment could not be loaded.',
      })
      return undefined
    }
    return result.payload
  }

  /** Edit of a draft deal. */
  async function editDeal() {
    if (loadingForm) return
    setLoadingForm(true)
    try {
      const [fetched] = await Promise.all([
        fetchDeal(row.status ?? 'Draft'),
        dispatch(loadDealFormLists() as any),
      ])
      if (!fetched) return
      setDeal(fetched)
      setDealAmendment(null)
      setAmendment(null)
      setOpenEdit(true)
    } finally {
      setLoadingForm(false)
    }
  }

  /**
   * Edit or amend an approved deal: the approved record, plus the amendment
   * already in flight when the row is one.
   */
  async function amendDeal(type: string) {
    if (loadingForm) return
    setLoadingForm(true)
    try {
      const [fetched, amended] = await Promise.all([
        fetchDeal('Approved'),
        row.amendment ? fetchAmendment() : Promise.resolve(null),
        dispatch(loadDealFormLists() as any),
      ])
      if (!fetched || (row.amendment && !amended)) return
      setDeal(fetched)
      setDealAmendment(amended ?? null)
      setAmendment(type)
      setOpenEdit(true)
    } finally {
      setLoadingForm(false)
    }
  }

  async function terminateDeal() {
    if (loadingForm) return
    setLoadingForm(true)
    try {
      const [fetched, amended] = await Promise.all([
        fetchDeal('Approved'),
        row.amendment ? fetchAmendment() : Promise.resolve(null),
      ])
      if (!fetched || (row.amendment && !amended)) return
      setDeal(fetched)
      setDealAmendment(amended ?? null)
      setOpenTerminate(true)
    } finally {
      setLoadingForm(false)
    }
  }

  function displayHistory(): any {
    setDeal(row)
    setOpenHistory(true)
  }

  /** Create Facility on this deal: the facility form's lists, with the modal showing a loader until they are in. */
  async function openFacilityForm() {
    if (loadingFacilityForm) return
    setLoadingFacilityForm(true)
    setOpenFacility(true)
    try {
      // the whole deal, not the grid row: the form reads its pricing-grid flag and
      // borrowers. An amendment row stands in for an APPROVED deal.
      const [fetched] = await Promise.all([
        fetchDeal(row.amendment ? 'Approved' : (row.status ?? 'Approved')),
        dispatch(loadFacilityFormLists() as any),
      ])
      if (!fetched) {
        setOpenFacility(false)
        return
      }
      setDeal(fetched)
    } finally {
      setLoadingFacilityForm(false)
    }
  }

  async function submitCurrentDeal() {
    const result = await submitDeal(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Submitted`)
    }
  }

  async function approveCurrentDeal() {
    const result = await approveDeal(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Approved`)
    }
  }

  async function rejectCurrentDeal() {
    const result = await rejectDeal(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Draft`)
    }
  }
  async function submitCurrentDealAmendment() {
    const result = await submitDealAmendment(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Subbmitted`)
    }
  }
  async function approveCurrentDealAmendment() {
    const result = await approveDealAmendment(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Approved`)
    }
  }
  async function rejectCurrentDealAmendment() {
    const result = await rejectDealAmendment(row, dispatch)
    if (result && withRedirection) {
      navigate(`/dealmanagement/deal/${row.id}/Draft`)
    }
  }

  const handleDelete = async () => {
    if (deleteType === 'deal') {
      await deleteDeal(row, dispatch);
      if (withRedirection) navigate(`/dealmanagement`);
    } else if (deleteType === 'amendment') {
      const result = await deleteDealAmendment(row, dispatch);
      if (result && withRedirection) navigate(`/dealmanagement/deal/${row.id}/Approved`);
    }
    setOpenDeleteModal(false); // Close modal
  };


  const openPreview = async (row: any) => {
    const dealSnapshot = await loadDealSnapshot(row.id)
    row = dealSnapshot.payload
    const { monthDayYear } = formatDateSnapshot();
    let previewHTML = ""
    previewHTML = `
<!DOCTYPE html>
  <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Deal & Facilities</title>
        <link rel="stylesheet" type="text/css" href="/previewStyles.css" />
    </head>
    <body>
        <div id="content">
            <header>
                <div>
                    <h1>Deal Snapshot</h1>
                </div>
                <div>As of ${monthDayYear}</div>
            </header>

            <main>
                <!-- Overview -->
                <div id="overview">
                    <div id="overview-container">
                        <div class="heading">
                            <h3>Deal Name: </h3>
                            <h2>${row.dealName}</h2>
                        </div>
                        <h3>Deal Details:</h3>
                        <table class="details-table details-overview">
                            <tbody>
                                <tr>
                                    <td><strong>Agent:</strong></td>
                                    <td>${row.agent}</td>
                                </tr>
                                 <tr>
                                    <td><strong>White Label Agent:</strong></td>
                                    <td>${row.whiteLabelAgent}</td>
                                </tr>
                                <tr>
                                    <td><strong>Borrowers:</strong></td>
                                    <td>${row.borrowers}</td>
                                </tr>
                                <tr>
                                    <td><strong>Guarantors:</strong></td>
                                    <td>${row.guarantors}</td>
                                </tr>
                                <tr>
                                    <td><strong>Original Amount:</strong></td>
                                    <td>${row.originalAmount ? formatNumberToCurrency(Number(row.originalAmount), row.currency) : ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>Current Amount:</strong></td>
                                    <td>${row.amount ? formatNumberToCurrency(Number(row.amount), row.currency) : ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>Currency:</strong></td>
                                    <td>${row.currency}</td>
                                </tr>
                            </tbody>
                        </table>
                        <table class="details-table details-overview">
                            <tbody>
                                <tr>
                                    <td><strong>Start Date:</strong></td>
                                    <td>${row.startDate}</td>
                                </tr>
                                <tr>
                                    <td><strong>Maturity Date:</strong></td>
                                    <td>${row.maturityDate}</td>
                                </tr>
                                <tr>
                                    <td><strong>Client ID:</strong></td>
                                    <td>${row.dealClientId ?? ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>Deal CUSIP</strong></td>
                                    <td>${row.dealCUSIP ?? ''}</td>
                                </tr>
                                 <tr>
                                    <td><strong>Deal ISIN</strong></td>
                                    <td>${row.dealISIN ?? ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>Deal LXID:</strong></td>
                                    <td>${row.dealLXID ?? ''}</td>
                                </tr>
                                 <tr>
                                    <td><strong>Deal BBG ID:</strong></td>
                                    <td>${row.dealBBGID ?? ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            ${row?.facilities?.map((facility: FacilitySnapshotParams, index: number) => generateHTML(facility, index + 1)).join('')}
            </main>
        </div>
    </body>
  </html>`;

    const newWindow = window.open("", "_blank")
    if (newWindow) {
      newWindow.document.write(previewHTML)
      newWindow.document.close()
    }
  }

  return (
    <div>
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        opened={openEdit}
        onClose={() => setOpenEdit(false)}
        centered={true}
        size="77%"
      >
        <DealsForm
          closeModal={() => setOpenEdit(false)}
          deal={deal ?? {}}
          useStepper={false}
          amendment={amendment}
          dealAmendment={dealAmendment}
        />
      </Modal>
      <Menu shadow="md" width={primaryInput ? '250px' : 200} position="bottom" withinPortal>
        <Menu.Target>
          <div style={{ width: 'fit-content' }}>
            {primaryInput ?
              <PrimaryInput w='159px'>
                {loadingForm ? 'Loading…' : 'Actions'}
                <IonIcon name='chevron-down-outline' className='primaryInputChevronDown' />
              </PrimaryInput>
              :
              <PrimaryDropDown>{loadingForm ? 'Loading…' : 'Actions'}</PrimaryDropDown>
            }
          </div>
        </Menu.Target>

        <Menu.Dropdown>
          {row?.status?.toLowerCase() == 'draft' && !row?.amendment ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
              onClick={() => editDeal()}
            >
              Edit
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'draft' && row?.amendment ? (
            row?.amendmentType?.toLowerCase() === 'termination' ? (
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                onClick={() => {
                  terminateDeal()
                }}
              >
                Edit
              </Menu.Item>
            ) : (
              <>
                <Menu.Item
                  icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                  onClick={() => {
                    amendDeal(row.amendmentDate === row.startDate ? 'edit' : 'amend')
                  }}
                >
                  Edit
                </Menu.Item>

              </>
            )
          ) : null}

          {row?.status?.toLowerCase() == 'approved' ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
              onClick={() => openPreview(row)}
            >
              Snapshot
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'draft' && row?.amendment ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => submitCurrentDealAmendment()}
            >
              Submit For Approval
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'draft' && !row?.amendment ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => submitCurrentDeal()}
            >
              Submit For Approval
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'submitted' && row?.amendment ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => approveCurrentDealAmendment()}
            >
              Approve
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'submitted' && !row?.amendment ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => approveCurrentDeal()}
            >
              Approve
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'approved' ||
            row?.amendment?.status?.toLowerCase() === '' ? (
            <>
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                onClick={() => {
                  amendDeal('edit')
                }}
              >
                Edit
              </Menu.Item>
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                onClick={() => {
                  amendDeal('amend')
                }}
              >
                Amendment
              </Menu.Item>
              <Menu.Item
                icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                onClick={() => {
                  terminateDeal()
                }}
              >
                Terminate
              </Menu.Item>
            </>
          ) : null}
          {extended ? (
            <Menu.Item
              icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
              onClick={() => openFacilityForm()}
            >
              Create Facility
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() != 'approved' ||
            row?.amendment?.status?.toLowerCase() === 'draft' ? (
            <>
              <Menu.Divider />
              <Menu.Label>Danger zone</Menu.Label>
            </>
          ) : null}

          {row?.status?.toLowerCase() == 'draft' && row?.amendment ? (
            <Menu.Item
              color="red"
              icon={<IonIcon className="action-ion-icon" name="trash-outline" />}
              onClick={() => {
                setDeleteType('amendment'); // Set delete type for amendment
                setOpenDeleteModal(true);
              }}
            >
              Delete
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'draft' && !row?.amendment ? (
            <Menu.Item
              color="red"
              icon={<IonIcon className="action-ion-icon" name="trash-outline" />}
              onClick={() => {
                setDeleteType('deal'); // Set delete type for deal
                setOpenDeleteModal(true);
              }}
            >
              Delete
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'submitted' && row?.amendment ? (
            <Menu.Item
              color="red"
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => rejectCurrentDealAmendment()}
            >
              Reject
            </Menu.Item>
          ) : null}

          {row?.status?.toLowerCase() == 'submitted' && !row?.amendment ? (
            <Menu.Item
              color="red"
              icon={<IonIcon className="action-ion-icon" name='swap-horizontal-outline' />}
              onClick={() => rejectCurrentDeal()}
            >
              Reject
            </Menu.Item>
          ) : null}
          <Menu.Item
            onClick={() => displayHistory()}
            icon={<IonIcon className="action-ion-icon" name='arrow-undo-circle-outline' />}
          >
            History
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        size="90%"
        opened={openHistory}
        onClose={() => setOpenHistory(false)}
      >
        {deal && (
          <HistoryForm
            dealId={deal.id ?? ''}
            close={() => setOpenHistory(false)}
          />
        )}
      </Modal>
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        size="xl"
        // style={{ height: '80%' }}
        opened={openTerminate}
        onClose={() => setOpenTerminate(false)}
      >
        {deal && (
          <DealTerminationForm
            deal={dealAmendment ?? deal}
            close={() => setOpenTerminate(false)}
          />
        )}
      </Modal>
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        opened={openFacility}
        onClose={() => setOpenFacility(false)}
        centered={true}
        size="77%"
      >
        {loadingFacilityForm ? (
          <CustomLoaderPage />
        ) : (
          <UpdatedFacilitiesScreen
            deal={deal ?? row}
            facility={undefined}
            onClose={() => setOpenFacility(false)}
          />
        )}
      </Modal>
      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <DeleteConfirmation
          onConfirm={handleDelete}
          onCancel={() => setOpenDeleteModal(false)}
          message={
            deleteType === 'deal'
              ? 'Delete This Deal?'
              : deleteType === 'amendment'
                ? 'Delete This Deal Amendment?'
                : 'Are you sure?'
          }
        />
      )}
    </div>
  )
}
